import { mm, TOLERANCE, type Um } from './units.js';
import type { Axis, Issue, PanelPart, Vec3 } from './model.js';
import type { AssemblyInstance } from './document.js';
import { makePanel, partBox } from './geometry.js';
import { DEFAULT_FRONT_CLEARANCE, frontOfPart } from './fronts.js';

/**
 * "Sklop pod kosinom": a modifier over the finished assembly. One face of the assembly box gets one
 * or two corner cuts along a chosen axis (like a cabinet under a roof slope). Every part is clipped by
 * the cut planes after all other systems (panels, shelves, drawers, fronts) have been built:
 *
 * - a part completely beyond a cut disappears;
 * - a part whose remaining outline (projected on its face) is still a rectangle just gets new
 *   dimensions (cut list), with a note when the cut runs through its thickness at an angle;
 * - any other outline becomes a CNC part with its polygon;
 * - panels lying on the cut face keep their flat middle, and a sloped closing panel is added along
 *   each cut (`closePanels`).
 *
 * Parts that do not reach the face (dividers under the top, shelves …) are cut by the plane moved
 * inwards by the thickness of the face panel, so they stay under the sloped panel.
 */

export type SlopeFace = 'top' | 'bottom' | 'left' | 'right' | 'front' | 'back';
export const SLOPE_FACES: SlopeFace[] = ['top', 'bottom', 'left', 'right', 'front', 'back'];
export const SLOPE_FACE_LABEL: Record<SlopeFace, string> = { top: 'Gornja', bottom: 'Donja', left: 'Lijeva', right: 'Desna', front: 'Prednja', back: 'Zadnja' };

/** A corner cut: how far along the face it reaches (run) and how far it goes down the side (drop). */
export interface SlopeCut {
  run: Um;
  drop: Um;
}

export interface AssemblySlope {
  face: SlopeFace;
  /** Axis along the face the cuts run on (never the face normal). */
  axis: Axis;
  /** Cut at the low end of the axis (left / bottom / back) and at the high end. */
  start?: SlopeCut;
  end?: SlopeCut;
  /** Add sloped panels along the cuts where a panel lies on the face. */
  closePanels: boolean;
  /** Extra length on every bevelled (sawn) end for cutting the slope later ("nadmjera"); CNC parts are cut exactly. */
  oversize?: Um;
  /** Made by "Prilagodi kosini" from a sloped wall (offered to be switched off when no longer needed). */
  fromWall?: boolean;
}

const IDX: Record<Axis, 0 | 1 | 2> = { x: 0, y: 1, z: 2 };
const NAME: Axis[] = ['x', 'y', 'z'];
export const FACE_NORMAL: Record<SlopeFace, { axis: Axis; sign: 1 | -1 }> = {
  top: { axis: 'y', sign: 1 },
  bottom: { axis: 'y', sign: -1 },
  right: { axis: 'x', sign: 1 },
  left: { axis: 'x', sign: -1 },
  front: { axis: 'z', sign: 1 },
  back: { axis: 'z', sign: -1 },
};

/** Axes a cut can run along on a face. */
export function slopeAxes(face: SlopeFace): Axis[] {
  return NAME.filter((a) => a !== FACE_NORMAL[face].axis);
}

export const SLOPE_AXIS_LABEL: Record<Axis, string> = { x: 'po širini (lijevo / desno)', y: 'po visini (dolje / gore)', z: 'po dubini (nazad / naprijed)' };
export const SLOPE_END_LABEL: Record<Axis, [string, string]> = { x: ['Lijevi ugao', 'Desni ugao'], y: ['Donji ugao', 'Gornji ugao'], z: ['Zadnji ugao', 'Prednji ugao'] };

export function defaultSlope(): AssemblySlope {
  return { face: 'top', axis: 'x', end: { run: mm(300), drop: mm(300) }, closePanels: true };
}


function box3(parts: PanelPart[]): { min: Vec3; max: Vec3 } | null {
  if (!parts.length) return null;
  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const p of parts) {
    const b = partBox(p);
    for (const k of [0, 1, 2] as const) {
      min[k] = Math.min(min[k], b.min[k]);
      max[k] = Math.max(max[k], b.min[k] + b.size[k]);
    }
  }
  return { min, max };
}

const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];

/** Sutherland–Hodgman: a convex polygon (3D points) clipped to n·p ≤ c. */
function clipPoly(poly: Vec3[], pl: { n: Vec3; c: number }): Vec3[] {
  const out: Vec3[] = [];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % poly.length]!;
    const da = dot(pl.n, a) - pl.c;
    const db = dot(pl.n, b) - pl.c;
    if (da <= 0) out.push(a);
    if ((da < 0 && db > 0) || (da > 0 && db < 0)) {
      const t = da / (da - db);
      out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]);
    }
  }
  return out;
}

/** Vertices of a box clipped by planes (each kept vertex lies on a box face). */
export function clipBox(min: Vec3, max: Vec3, planes: { n: Vec3; c: number }[]): Vec3[] {
  const faces: Vec3[][] = [];
  for (const k of [0, 1, 2] as const)
    for (const v of [min[k], max[k]]) {
      const [u, w] = ([0, 1, 2] as const).filter((i) => i !== k) as [0 | 1 | 2, 0 | 1 | 2];
      const pt = (a: number, b: number) => {
        const p: Vec3 = [0, 0, 0];
        p[k] = v;
        p[u] = a;
        p[w] = b;
        return p;
      };
      faces.push([pt(min[u], min[w]), pt(max[u], min[w]), pt(max[u], max[w]), pt(min[u], max[w])]);
    }
  const pts: Vec3[] = [];
  for (let f of faces) {
    for (const pl of planes) f = clipPoly(f, pl);
    pts.push(...f);
  }
  return pts;
}

/** Convex hull (monotone chain) of 2D points, counter-clockwise. */
function hull(points: [number, number][]): [number, number][] {
  const p = [...new Map(points.map((q) => [`${Math.round(q[0])},${Math.round(q[1])}`, [Math.round(q[0]), Math.round(q[1])] as [number, number]])).values()].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (p.length < 3) return p;
  const cross = (o: [number, number], a: [number, number], b: [number, number]) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: [number, number][] = [];
  for (const q of p) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, q) <= 0) lower.pop();
    lower.push(q);
  }
  const upper: [number, number][] = [];
  for (const q of [...p].reverse()) {
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, q) <= 0) upper.pop();
    upper.push(q);
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

const SMALL = mm(1);

/** A 2D point / line in the plane of the slope: a = along the axis, n = along the face normal. */
type P2 = { a: number; n: number };
/** Line n·x = c with unit normal. */
type L2 = { na: number; nn: number; c: number };

const lineThrough = (p: P2, q: P2, towards: P2): L2 => {
  const da = q.a - p.a;
  const dn = q.n - p.n;
  const len = Math.hypot(da, dn) || 1;
  let na = -dn / len;
  let nn = da / len;
  if (na * (towards.a - p.a) + nn * (towards.n - p.n) < 0) [na, nn] = [-na, -nn];
  return { na, nn, c: na * p.a + nn * p.n };
};
const meet = (l: L2, m: L2): P2 | null => {
  const det = l.na * m.nn - l.nn * m.na;
  if (Math.abs(det) < 1e-12) return null;
  return { a: (l.c * m.nn - l.nn * m.c) / det, n: (l.na * m.c - l.c * m.na) / det };
};
const dist2 = (p: P2, q: P2) => Math.hypot(p.a - q.a, p.n - q.n);

interface Cut2 {
  end: 'start' | 'end';
  edgeA: number;
  /** Outer face of the slope (the cut itself) and the inner face of a closing panel. */
  outer: L2;
  inner: L2;
  p1: P2;
}

/**
 * Applies the slope to the built parts (changed parts are replaced). Returns the parts and the issues;
 * plinth, fillers and legs are added later and are not cut.
 *
 * Joints of the closing panels: at the flat face panel a mitre (the line through the outer and the
 * inner corner), at the side panel at the cut end a square stop against its inner face (the side is
 * cut by the slope itself, so its top continues the slope), and where two slopes meet a ridge mitre.
 */
export interface SlopeResult {
  parts: PanelPart[];
  issues: Issue[];
  modified: Set<PanelPart>;
  /** The outer cut planes (keep n·p ≤ c), for parts added later (plinth, fillers). */
  planes: { n: Vec3; c: number }[];
  /** A board added later on the cut face, in the plane of the cut (e.g. the top filler): a band of the
   *  given height following the outline (flat, then down the slope). Null when the slope has no cuts. */
  band: ((p: PanelPart, height: number, oversize?: number) => PanelPart[]) | null;
  /**
   * A filler beside the carcass at one end of the slope axis (side filler under a top slope): it is
   * raised and cut by the outline line at that carcass edge, continued past the carcass (`shift` moves
   * it out, for a filler that runs up to the strip's upper edge). Null when not applicable.
   */
  edgeCut: ((p: PanelPart, end: 'start' | 'end', shift: number, oversize?: number) => PanelPart | null) | null;
  /** Slope axis (along the face) and face normal axis. */
  axes: { along: Axis; normal: Axis } | null;
}

export function applySlope(inst: AssemblyInstance, parts: PanelPart[]): SlopeResult {
  const s = inst.slope;
  const issues: Issue[] = [];
  const modified = new Set<PanelPart>();
  const planes: { n: Vec3; c: number }[] = [];
  let band: SlopeResult['band'] = null;
  let edgeCut: SlopeResult['edgeCut'] = null;
  let axes: SlopeResult['axes'] = null;
  const res = () => ({ parts, issues, modified, planes, band, edgeCut, axes });
  if (!s) return res();
  const err = (message: string) => issues.push({ severity: 'error', code: 'slope.invalid', message: `Kosina: ${message}`, cabinetId: inst.id });
  if (s.axis === FACE_NORMAL[s.face].axis) err('osa kosine ne može biti okomita na stranicu.');
  for (const end of ['start', 'end'] as const) {
    const c = s[end];
    if (c && (!Number.isInteger(c.run) || !Number.isInteger(c.drop) || c.run <= 0 || c.drop <= 0 || c.run > mm(10000) || c.drop > mm(10000))) err('dužina i spuštanje moraju biti 1–10000 mm.');
  }
  const box = box3(parts);
  if (issues.length || !box) return res();
  const N = IDX[FACE_NORMAL[s.face].axis];
  const sN = FACE_NORMAL[s.face].sign;
  const A = IDX[s.axis];
  const O = ([0, 1, 2] as const).find((k) => k !== N && k !== A)!;
  const F = sN > 0 ? box.max[N] : box.min[N];
  const lenA = box.max[A] - box.min[A];
  const lenN = box.max[N] - box.min[N];
  const farN = (p: PanelPart) => {
    const b = partBox(p);
    return sN > 0 ? b.min[N] + b.size[N] : b.min[N];
  };
  const reaches = (p: PanelPart) => Math.abs(farN(p) - F) <= TOLERANCE.contact;
  const facePanels = s.closePanels ? parts.filter((p) => reaches(p) && p.placement.thicknessAxis === NAME[N] && p.role !== 'door') : [];
  const t = facePanels.length ? Math.max(...facePanels.map((p) => p.thickness)) : 0;
  const faceOuter: L2 = { na: 0, nn: sN, c: sN * F };
  const faceInner: L2 = { na: 0, nn: sN, c: sN * F - t };

  // The cuts, limited to the assembly (a longer cut would start outside it).
  const cuts: Cut2[] = [];
  for (const end of ['start', 'end'] as const) {
    const c = s[end];
    if (!c) continue;
    // The angle is kept: a cut longer than the face just runs over all of it (lower at the far end);
    // a drop deeper than the assembly is limited, with the length scaled to keep the angle.
    const drop = Math.min(c.drop, lenN);
    const run = (c.run * drop) / c.drop;
    if (drop < c.drop)
      issues.push({ severity: 'warning', code: 'slope.clamped', message: `Kosina: ${SLOPE_END_LABEL[s.axis][end === 'start' ? 0 : 1].toLowerCase()} se spušta niže od sklopa — uzeto ${Math.round(run) / 1000} × ${drop / 1000} mm (isti ugao).`, cabinetId: inst.id });
    const edgeA = end === 'start' ? box.min[A] : box.max[A];
    const dirA = end === 'start' ? 1 : -1;
    const p1: P2 = { a: edgeA + dirA * run, n: F };
    const p2: P2 = { a: edgeA, n: F - sN * drop };
    const outer = lineThrough(p2, p1, { a: edgeA, n: F });
    cuts.push({ end, edgeA, outer, inner: { ...outer, c: outer.c - t }, p1 });
  }
  if (!cuts.length) return res();
  const plane3 = (l: L2, shift = 0): { n: Vec3; c: number } => {
    const n: Vec3 = [0, 0, 0];
    n[A] = l.na;
    n[N] = l.nn;
    return { n, c: l.c - shift };
  };
  // Top end of each closing panel: the mitre with the flat face panel, or the ridge with the other slope.
  const ridge = cuts.length === 2 ? { o: meet(cuts[0]!.outer, cuts[1]!.outer), i: meet(cuts[0]!.inner, cuts[1]!.inner) } : null;
  const qOf = (c: Cut2) => meet(c.inner, faceInner)!;
  const isRidge = !!(t && ridge?.o && ridge.i && cuts.length === 2 && qOf(cuts[0]!).a >= qOf(cuts[1]!).a);
  // Square stops: the inner face of a side panel at an end of the axis that reaches the face, else the box edge.
  const stopAt = (end: 'start' | 'end') => {
    const edgeA = end === 'start' ? box.min[A] : box.max[A];
    const dir = end === 'start' ? 1 : -1;
    const ends = parts.filter((p) => {
      if (p.placement.thicknessAxis !== NAME[A] || !reaches(p) || p.role === 'door') return false;
      const b = partBox(p);
      return Math.abs((end === 'start' ? b.min[A] : b.min[A] + b.size[A]) - edgeA) <= TOLERANCE.contact;
    });
    return ends.length ? edgeA + dir * Math.max(...ends.map((p) => p.thickness)) : edgeA;
  };
  const stops = { start: stopAt('start'), end: stopAt('end') };
  // A flat part of the face is left beside this cut (else the slope runs over the whole face).
  const flat = (c: Cut2) => {
    const q = qOf(c);
    return c.end === 'start' ? q.a < stops.end : q.a > stops.start;
  };
  const squareAt = (c: Cut2, a: number) => ({ o: meet(c.outer, { na: 1, nn: 0, c: a })!, i: meet(c.inner, { na: 1, nn: 0, c: a })! });
  const top = (c: Cut2): { o: P2; i: P2 } =>
    isRidge ? { o: ridge!.o!, i: ridge!.i! } : flat(c) ? { o: c.p1, i: qOf(c) } : squareAt(c, c.end === 'start' ? stops.end : stops.start);
  const mitre = (c: Cut2) => {
    const tp = top(c);
    return lineThrough(tp.o, tp.i, { a: c.edgeA, n: F });
  };

  const outerPlanes = cuts.map((c) => plane3(c.outer));
  planes.push(...outerPlanes);
  band = (p, height, oversize = 0) => {
    // Only a board lying in the plane of the cut (its thickness across it); otherwise just cut it.
    if (p.placement.thicknessAxis !== NAME[O] || p.placement.lengthAxis !== NAME[A]) {
      const r = clipPart(p, planes.map((pl) => ({ n: pl.n, c: pl.c + height })), oversize);
      return r ? [r] : [];
    }
    const b = partBox(p);
    const a0 = b.min[A];
    const a1 = b.min[A] + b.size[A];
    // Outline lines as heights H(a) = k·a + m, measured outwards along the face normal: the face and
    // the slopes. The filler is a strip of `height` over the lowest of them.
    type Ln = { k: number; m: number; up: number };
    const lines: Ln[] = [{ k: 0, m: sN * F, up: height }, ...cuts.map((c) => ({ k: -c.outer.na / (c.outer.nn * sN), m: c.outer.c / (c.outer.nn * sN), up: height / (c.outer.nn * sN) }))];
    const at = (l: Ln, a: number) => l.k * a + l.m;
    // Past the carcass ends (over side fillers) the outline continues the line at the carcass edge.
    const cA0 = box.min[A];
    const cA1 = box.max[A];
    const active = (a: number) => {
      const x = Math.min(cA1, Math.max(cA0, a));
      return lines.reduce((best, l) => (at(l, x) < at(best, x) ? l : best));
    };
    const xs = new Set<number>([a0, a1]);
    for (let i = 0; i < lines.length; i++)
      for (let j = i + 1; j < lines.length; j++) {
        const dk = lines[i]!.k - lines[j]!.k;
        if (Math.abs(dk) < 1e-12) continue;
        const a = (lines[j]!.m - lines[i]!.m) / dk;
        if (a > Math.max(a0, cA0) + SMALL && a < Math.min(a1, cA1) - SMALL) xs.add(a);
      }
    const as = [...xs].sort((x, y) => x - y);
    // Segments: runs of the same outline line.
    const segs: { l: Ln; s: number; e: number }[] = [];
    for (let i = 0; i + 1 < as.length; i++) {
      const l = active((as[i]! + as[i + 1]!) / 2);
      const last = segs[segs.length - 1];
      if (last && last.l === l) last.e = as[i + 1]!;
      else segs.push({ l, s: as[i]!, e: as[i + 1]! });
    }
    const out: PanelPart[] = [];
    const meetUp = (l: Ln, o: Ln) => (o.m + o.up - (l.m + l.up)) / (l.k - o.k);
    segs.forEach((g, i) => {
      // Lower corners on the outline; upper corners where neighbouring strips' upper edges meet (mitre),
      // square at the free ends.
      const prev = segs[i - 1];
      const next = segs[i + 1];
      const lo0: P2 = { a: g.s, n: sN * at(g.l, g.s) };
      const lo1: P2 = { a: g.e, n: sN * at(g.l, g.e) };
      const ua0 = prev ? meetUp(g.l, prev.l) : g.s;
      const ua1 = next ? meetUp(g.l, next.l) : g.e;
      const up0: P2 = { a: ua0, n: sN * (at(g.l, ua0) + g.l.up) };
      const up1: P2 = { a: ua1, n: sN * (at(g.l, ua1) + g.l.up) };
      const poly: P2[] = [lo0, lo1, up1, up0];
      // Raw board along the strip (oversize on mitred ends).
      const ul = Math.hypot(1, sN * g.l.k);
      const u = { a: 1 / ul, n: (sN * g.l.k) / ul };
      const proj = poly.map((v) => (v.a - lo0.a) * u.a + (v.n - lo0.n) * u.n);
      const s0 = Math.min(...proj) - (prev ? oversize : 0);
      const s1 = Math.max(...proj) + (next ? oversize : 0);
      const nrm = { a: -u.n * sN, n: u.a * sN }; // outwards, across the strip
      const flip = nrm.a * (up0.a - lo0.a) + nrm.n * (up0.n - lo0.n) < 0 ? -1 : 1;
      const pt = (sv: number, w: number): P2 => ({ a: lo0.a + u.a * sv + flip * nrm.a * w, n: lo0.n + u.n * sv + flip * nrm.n * w });
      const stock = [pt(s0, 0), pt(s1, 0), pt(s1, height), pt(s0, height)];
      const min: Vec3 = [...b.min];
      const size: Vec3 = [...b.size];
      min[A] = Math.round(Math.min(...poly.map((v) => v.a)));
      min[N] = Math.round(Math.min(...poly.map((v) => v.n)));
      size[A] = Math.round(s1 - s0);
      size[N] = Math.round(height);
      const key = i === 0 ? p.localKey : `${p.localKey}-${i + 1}`;
      const q = makePanel({ cabinetId: p.cabinetId, localKey: key, role: p.role, label: segs.length > 1 ? `${p.label} ${i + 1}` : p.label, box: { min, size }, lengthAxis: NAME[A]!, widthAxis: NAME[N]!, materialSlot: p.materialSlot, grain: p.grain });
      q.profile = { axes: [NAME[A]!, NAME[N]!], points: poly.map((v) => [Math.round(v.a), Math.round(v.n)] as [number, number]), from: Math.round(b.min[O]), to: Math.round(b.min[O] + b.size[O]) };
      q.stock = { ...q.profile, points: stock.map((v) => [Math.round(v.a), Math.round(v.n)] as [number, number]) };
      const deg = Math.round((Math.atan(Math.abs(g.l.k)) * 180) / Math.PI);
      const ends = [prev, next].filter(Boolean).length;
      q.cutNote = [deg ? `Blenda uz kosinu ${deg}°.` : '', ends ? `Kosi spoj (pila) na ${ends === 2 ? 'oba kraja' : 'jednom kraju'}${oversize ? `, nadmjera ${oversize / 1000} mm` : ''}.` : ''].filter(Boolean).join(' ') || undefined;
      out.push(q);
    });
    return out;
  };
  const innerPlanes = cuts.map((c) => plane3(c.inner));
  const out: PanelPart[] = [];
  for (const p of parts) {
    // Face panels: their flat part up to the mitre (none left under a ridge); parts reaching the face,
    // fronts and everything when not closing: the cut itself; inner parts: under the closing panels.
    let ps: { n: Vec3; c: number }[];
    if (facePanels.includes(p)) {
      if (isRidge || cuts.some((c) => !flat(c))) {
        modified.add(p);
        continue;
      }
      ps = cuts.map((c) => plane3(mitre(c)));
    } else if (p.role === 'door') {
      // Fronts keep their clearance (zazor) along the slope too.
      const f = frontOfPart(inst, p.localKey);
      const gap = f?.clearance ?? DEFAULT_FRONT_CLEARANCE;
      ps = outerPlanes.map((pl) => ({ n: pl.n, c: pl.c - gap }));
    } else ps = !t || reaches(p) ? outerPlanes : innerPlanes;
    const r = clipPart(p, ps, s.oversize ?? 0);
    if (r === p) out.push(p);
    else {
      modified.add(p);
      if (r) {
        out.push(r);
        modified.add(r);
      }
    }
  }

  // Closing panels along the cuts, as their real profile (mitred top, square stop at the side panel).
  if (t) {
    const fb = partBox(facePanels[0]!);
    const o0 = Math.min(...facePanels.map((p) => partBox(p).min[O]));
    const o1 = Math.max(...facePanels.map((p) => partBox(p).min[O] + partBox(p).size[O]));
    void fb;
    for (const c of cuts) {
      // Side panel at this end reaching the face: the closing panel stops at its inner face.
      const stop: L2 = { na: 1, nn: 0, c: stops[c.end] };
      const tp = top(c);
      const bo = meet(c.outer, stop);
      const bi = meet(c.inner, stop);
      if (!bo || !bi) continue;
      const poly: P2[] = [tp.o, bo, bi, tp.i];
      if (Math.max(dist2(tp.o, bo), dist2(tp.i, bi)) < mm(5)) continue;
      // Raw board ("stvarni rez"): a rectangle along the slope holding the whole profile, plus the
      // oversize at both (angled) ends; its length goes to the cut list.
      const ua = bo.a - tp.o.a;
      const un = bo.n - tp.o.n;
      const ul = Math.hypot(ua, un) || 1;
      const u = { a: ua / ul, n: un / ul };
      const proj = poly.map((v) => (v.a - tp.o.a) * u.a + (v.n - tp.o.n) * u.n);
      const ov = s.oversize ?? 0;
      const s0 = Math.min(...proj) - ov;
      const s1 = Math.max(...proj) + ov;
      const len = s1 - s0;
      const at = (sv: number, inward: number): P2 => ({ a: tp.o.a + u.a * sv - c.outer.na * inward, n: tp.o.n + u.n * sv - c.outer.nn * inward });
      const stock: P2[] = [at(s0, 0), at(s1, 0), at(s1, t), at(s0, t)];
      const as = poly.map((q) => q.a);
      const ns = poly.map((q) => q.n);
      const min: Vec3 = [0, 0, 0];
      const size: Vec3 = [0, 0, 0];
      min[A] = Math.round(Math.min(...as));
      min[N] = Math.round(Math.min(...ns));
      min[O] = Math.round(o0);
      size[A] = Math.round(len);
      size[N] = Math.round(t);
      size[O] = Math.round(o1 - o0);
      const k = c.end === 'start' ? 1 : 2;
      const fp = facePanels[0]!;
      const q = makePanel({
        cabinetId: inst.id,
        localKey: `kosina-${k}`,
        role: 'custom',
        label: `Kosa ploča ${k}`,
        box: { min, size },
        lengthAxis: NAME[A]!,
        widthAxis: NAME[O]!,
        materialSlot: fp.materialSlot,
        grain: fp.grain,
      });
      q.profile = { axes: [NAME[A]!, NAME[N]!], points: poly.map((v) => [Math.round(v.a), Math.round(v.n)] as [number, number]), from: Math.round(o0), to: Math.round(o1) };
      q.stock = { ...q.profile, points: stock.map((v) => [Math.round(v.a), Math.round(v.n)] as [number, number]) };
      const deg = (x: number) => Math.round((x * 180) / Math.PI);
      // Angles of the ends from a square cut (the panel's normal is the slope's normal).
      const bevel = (u: P2, v: P2) => {
        const da = v.a - u.a;
        const dn = v.n - u.n;
        const l = Math.hypot(da, dn) || 1;
        return deg(Math.acos(Math.min(1, Math.abs((da * c.outer.na + dn * c.outer.nn) / l))));
      };
      q.cutNote = `Kosa ploča pod ${deg(Math.atan2(Math.abs(c.outer.na), Math.abs(c.outer.nn)))}°; gornji kraj ${bevel(tp.o, tp.i)}°, donji kraj ${bevel(bo, bi)}° od pravog reza${ov ? `; uključena nadmjera ${ov / 1000} mm po kraju` : ''}.`;
      out.push(q);
      modified.add(q);
    }
  }
  if (modified.size) issues.push({ severity: 'info', code: 'slope.applied', message: `Kosina: ${modified.size} dijelova izmijenjeno/dodano${[...modified].some((p) => p.cnc) ? ' — neki idu na CNC' : ''}.`, cabinetId: inst.id });
  axes = { along: NAME[A]!, normal: NAME[N]! };
  edgeCut = (p, end, shift, oversize = 0) => {
    // The outline line at that carcass edge (the face, or the slope reaching that edge), as a plane.
    // The line that is the outline just inside the carcass edge.
    const a = end === 'start' ? box.min[A] + mm(1) : box.max[A] - mm(1);
    const H = (l: L2) => (l.c - l.na * a) / (l.nn * sN);
    const slope = cuts.filter((c) => H(c.outer) < sN * F - 1).sort((x, y) => H(x.outer) - H(y.outer))[0];
    const pl = slope ? { ...plane3(slope.outer), c: plane3(slope.outer).c + shift } : (() => {
      const n: Vec3 = [0, 0, 0];
      n[N] = sN;
      return { n, c: sN * F + shift };
    })();
    // Raised so the cut line (which may climb past the carcass) is inside it.
    const b = partBox(p);
    const min: Vec3 = [...b.min];
    const size: Vec3 = [...b.size];
    const extra = box.max[A] - box.min[A] + shift + mm(1000);
    if (sN < 0) min[N] -= extra;
    size[N] += extra;
    const tall = makePanel({ cabinetId: p.cabinetId, localKey: p.localKey, role: p.role, label: p.label, box: { min, size }, lengthAxis: p.placement.lengthAxis, widthAxis: p.placement.widthAxis, materialSlot: p.materialSlot, grain: p.grain });
    return clipPart(tall, [pl], oversize);
  };
  return { parts: out, issues, modified, planes, band, edgeCut, axes };
}

/** A part added after the slope (plinth, filler) cut by its planes; null when nothing is left. */
export function clipToSlope(p: PanelPart, planes: { n: Vec3; c: number }[], oversize = 0): PanelPart | null {
  return planes.length ? clipPart(p, planes, oversize) : p;
}

/** One part clipped by planes: the same part, a new (smaller / CNC) part, or null when nothing is left. */
function clipPart(p: PanelPart, ps: { n: Vec3; c: number }[], oversize = 0): PanelPart | null {
  const b = partBox(p);
  const pmin = b.min;
  const pmax: Vec3 = [b.min[0] + b.size[0], b.min[1] + b.size[1], b.min[2] + b.size[2]];
  const corners: Vec3[] = [];
  for (const x of [pmin[0], pmax[0]]) for (const y of [pmin[1], pmax[1]]) for (const z of [pmin[2], pmax[2]]) corners.push([x, y, z]);
  if (corners.every((q) => ps.every((pl) => dot(pl.n, q) - pl.c <= SMALL))) return p;
  const pts = clipBox(pmin, pmax, ps);
  if (pts.length < 3) return null;
  const L = IDX[p.placement.lengthAxis];
  const W = IDX[p.placement.widthAxis];
  const T = IDX[p.placement.thicknessAxis];
  const h = hull(pts.map((q) => [q[L], q[W]]));
  if (h.length < 3) return null;
  const l0 = Math.min(...h.map((q) => q[0]));
  const l1 = Math.max(...h.map((q) => q[0]));
  const w0 = Math.min(...h.map((q) => q[1]));
  const w1 = Math.max(...h.map((q) => q[1]));
  if (l1 - l0 < SMALL * 5 || w1 - w0 < SMALL * 5) return null;
  const poly = h.map(([u, v]) => [u - l0, v - w0] as [number, number]);
  const rect = poly.every(([u, v]) => (u <= SMALL || u >= l1 - l0 - SMALL) && (v <= SMALL || v >= w1 - w0 - SMALL));
  // A sawn (non-CNC) part: the raw board is the largest extent of the cut, plus the oversize on each
  // cut end (never more than the uncut part).
  let [a0, a1, b0, b1] = [l0, l1, w0, w1];
  if (rect && oversize > 0) {
    if (l0 > pmin[L] + SMALL) a0 = Math.max(pmin[L], l0 - oversize);
    if (l1 < pmax[L] - SMALL) a1 = Math.min(pmax[L], l1 + oversize);
    if (w0 > pmin[W] + SMALL) b0 = Math.max(pmin[W], w0 - oversize);
    if (w1 < pmax[W] - SMALL) b1 = Math.min(pmax[W], w1 + oversize);
  }
  const min: Vec3 = [...pmin];
  min[L] = Math.round(a0);
  min[W] = Math.round(b0);
  const size: Vec3 = [...b.size];
  size[L] = Math.round(a1 - a0);
  size[W] = Math.round(b1 - b0);
  const q = makePanel({
    cabinetId: p.cabinetId,
    localKey: p.localKey,
    role: p.role,
    label: p.label,
    box: { min, size },
    lengthAxis: p.placement.lengthAxis,
    widthAxis: p.placement.widthAxis,
    materialSlot: p.materialSlot,
    grain: p.grain,
    inCutList: p.inCutList,
  });
  q.connections = [...p.connections];
  // The real solid (with the bevel through the thickness) for drawing.
  q.solid = [...new Map(pts.map((v) => [v.map((x) => Math.round(x)).join(','), v.map((x) => Math.round(x)) as Vec3])).values()];
  if (p.movable) q.movable = p.movable;
  if (p.hingeSide) q.hingeSide = p.hingeSide;
  // A cut through the thickness at an angle: a bevel (angle from a square cut).
  const used = ps.filter((pl) => pts.some((v) => Math.abs(dot(pl.n, v) - pl.c) <= SMALL));
  const bevels = [...new Set(used.map((pl) => Math.round((Math.asin(Math.min(1, Math.abs(pl.n[T]))) * 180) / Math.PI)).filter((d) => d > 0 && d < 90))];
  const note = bevels.length ? `Kosi rez ${bevels.map((d) => `${d}°`).join(', ')} (kroz debljinu).` : undefined;
  if (!rect) q.cnc = { outline: [{ x0: 0, y0: 0, x1: size[L], y1: size[W] }], polygon: poly.map(([u, v]) => [Math.round(u), Math.round(v)] as [number, number]), ...(note ? { note } : {}) };
  else if (note || a1 - a0 > l1 - l0 + SMALL || b1 - b0 > w1 - w0 + SMALL)
    q.cutNote = [note, a1 - a0 > l1 - l0 + SMALL || b1 - b0 > w1 - w0 + SMALL ? `Uključena nadmjera ${oversize / 1000} mm za kosi rez.` : ''].filter(Boolean).join(' ');
  return q;
}
